// src/redux/supplier/supplierSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiServices from "../../services/apiServices";

// Fetch suppliers - Async Thunk with pagination and filters
export const fetchSuppliers = createAsyncThunk(
  "suppliers/fetchSuppliers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = "", sort = {} } = params;
      let url = `suppliers?page=${page}&limit=${limit}`;

      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (sort.key) {
        url += `&sortBy=${sort.key}&sortOrder=${sort.direction}`;
      }

      const response = await apiServices.get(url);
      return response.data; // This now returns { data, total, page, limit, totalPages }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch suppliers");
    }
  }
);

// Add new supplier - Async Thunk
export const addSupplier = createAsyncThunk(
  "suppliers/addSupplier",
  async (supplierData, { rejectWithValue }) => {
    try {
      const response = await apiServices.post("suppliers", supplierData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Update supplier - Async Thunk
export const updateSupplier = createAsyncThunk(
  "suppliers/updateSupplier",
  async (supplierData, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`suppliers/${supplierData._id}`, supplierData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete supplier - Async Thunk
export const deleteSupplier = createAsyncThunk(
  "suppliers/deleteSupplier",
  async (supplierId, { rejectWithValue }) => {
    try {
      await apiServices.delete(`suppliers/${supplierId}`);
      return supplierId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Search suppliers for dropdowns
export const searchSuppliersAsync = createAsyncThunk(
  "suppliers/searchSuppliersAsync",
  async (query, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`suppliers/search?query=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Redux Slice
const supplierSlice = createSlice({
  name: "suppliers",
  initialState: {
    suppliers: [],
    total: 0,
    currentPage: 1,
    totalPages: 1,
    loading: false,
    searching: false,
    searchResults: [],
    error: null,
    supplierAdded: false
  },
  reducers: {
    resetError: (state) => {
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    // Fetch suppliers
    builder.addCase(fetchSuppliers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSuppliers.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      // Handle both old array response and new paginated object response
      if (action.payload.data && Array.isArray(action.payload.data)) {
        state.suppliers = action.payload.data;
        state.total = action.payload.total || action.payload.data.length;
        state.currentPage = action.payload.page || 1;
        state.totalPages = action.payload.totalPages || 1;
      } else if (Array.isArray(action.payload)) {
        state.suppliers = action.payload;
        state.total = action.payload.length;
      }
    });
    builder.addCase(fetchSuppliers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Add new supplier
    builder.addCase(addSupplier.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addSupplier.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.supplierAdded = true;
      // Note: In server-side mode, we usually refetch instead of pushing
      // but keeping this for immediate UI feedback if needed
      if (Array.isArray(state.suppliers)) {
        state.suppliers.unshift(action.payload);
      }
    });
    builder.addCase(addSupplier.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.supplierAdded = false;
    });

    // Update supplier
    builder.addCase(updateSupplier.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateSupplier.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      const index = state.suppliers.findIndex(
        (supplier) => supplier._id === action.payload._id
      );
      if (index !== -1) {
        state.suppliers[index] = action.payload;
      }
    });
    builder.addCase(updateSupplier.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Delete supplier
    builder.addCase(deleteSupplier.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteSupplier.fulfilled, (state, action) => {
      state.loading = false;
      state.suppliers = state.suppliers.filter(
        (supplier) => supplier._id !== action.payload
      );
    });
    builder.addCase(deleteSupplier.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Search suppliers
    builder.addCase(searchSuppliersAsync.pending, (state) => {
      state.searching = true;
    });
    builder.addCase(searchSuppliersAsync.fulfilled, (state, action) => {
      state.searching = false;
      state.searchResults = action.payload;
    });
    builder.addCase(searchSuppliersAsync.rejected, (state) => {
      state.searching = false;
    });
  },
});
export const { resetError } = supplierSlice.actions;
export default supplierSlice.reducer;
